import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Chip,
    useTheme,
    CircularProgress,
} from '@mui/material';
import { collectionPointsService, CollectionPoint, CollectionPointLabelMap } from '../../services/collectionpoints-service';
import { labelsService, BaseLabel } from '../../services/labels-service';

interface LabelsTabProps {
    collectionPoint: CollectionPoint | null;
    isPublished: boolean;
    onLabelsChanged?: () => void; // Optional callback to notify parent of changes
}

const LabelsTab: React.FC<LabelsTabProps> = ({ collectionPoint, isPublished, onLabelsChanged }) => {
    const theme = useTheme();
    
    // Labels-related state
    const [labelMappings, setLabelMappings] = useState<CollectionPointLabelMap[]>([]);
    const [loadingLabels, setLoadingLabels] = useState(false);
    const [availableLabels, setAvailableLabels] = useState<BaseLabel[]>([]);

    
    // Labels-related useEffect hooks
    useEffect(() => {
        if (collectionPoint?.CollectionPointId) {
            setLoadingLabels(true);
            collectionPointsService.getMappedLabels(collectionPoint.CollectionPointId)
                .then(mappings => {
                    setLabelMappings(mappings);
                    setLoadingLabels(false);
                });
        }
    }, [collectionPoint]);

    useEffect(() => {
        if (!isPublished) {
            labelsService.getBaseLabels({ isPublished: true })
                .then(labels => {
                    const mappedLabelIds = labelMappings.map(m => m.LabelId);
                    // Exclude already mapped labels
                    setAvailableLabels(labels.labels.filter(l => 
                        !mappedLabelIds.includes(l.LabelId)
                    ));
                });
        }
    }, [collectionPoint, labelMappings, isPublished]);


    
    // Labels-related handler functions
    const handleMapLabel = async (labelId: number) => {
        if (!collectionPoint?.CollectionPointId) return;
        try {
            // Call API to map the label
            await collectionPointsService.mapLabel(collectionPoint.CollectionPointId, labelId);
            
            // Fetch fresh data from server
            const freshMappings = await collectionPointsService.getMappedLabels(collectionPoint.CollectionPointId);
            setLabelMappings(freshMappings);
            
            if (onLabelsChanged) {
                onLabelsChanged();
            }
        } catch (error) {
            console.error('Error mapping label:', error);
        }
    };

    const handleUnmapLabel = async (labelId: number) => {
        if (!collectionPoint?.CollectionPointId) return;
        try {
            // Call API to unmap the label
            await collectionPointsService.unmapLabel(collectionPoint.CollectionPointId, labelId);
            
            // Fetch fresh data from server
            const freshMappings = await collectionPointsService.getMappedLabels(collectionPoint.CollectionPointId);
            setLabelMappings(freshMappings);
            
            if (onLabelsChanged) {
                onLabelsChanged();
            }
        } catch (error) {
            console.error('Error unmapping label:', error);
        }
    };
    
    return (
        <Box sx={{ p: 3 }}>
            {loadingLabels ? (
                <Typography color="text.primary">Loading labels...</Typography>
            ) : (
                <>
                    <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary }}>
                        Mapped Labels
                    </Typography>
                    {labelMappings.length === 0 ? (
                        <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                            No labels mapped to this collection point.
                        </Typography>
                    ) : (
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                            {labelMappings.map((mapping) => (
                                <Box key={mapping.MappingId} sx={{
                                    minWidth: 320,
                                    maxWidth: 400,
                                    flex: '1 1 320px',
                                    bgcolor: theme.palette.background.paper,
                                    borderRadius: theme.shape.borderRadius,
                                    boxShadow: theme.shadows[2],
                                    p: 3,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: 1.5,
                                    border: `1px solid ${theme.palette.divider}`,
                                    position: 'relative',
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Typography variant="h6" color="text.primary" sx={{ flex: 1 }}>{mapping.LabelKey}</Typography>
                                        {!isPublished && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => handleUnmapLabel(mapping.LabelId)}
                                                sx={{
                                                    borderColor: theme.palette.error.main,
                                                    color: theme.palette.error.main,
                                                    '&:hover': {
                                                        borderColor: theme.palette.error.dark,
                                                        bgcolor: theme.palette.error.light,
                                                    },
                                                }}
                                            >
                                                Remove
                                            </Button>
                                        )}
                                    </Box>
                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {mapping.LabelText}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                        <Chip 
                                            label={mapping.LabelType} 
                                            size="small" 
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                        <Chip 
                                            label={mapping.Category} 
                                            size="small" 
                                            variant="outlined"
                                            sx={{ fontSize: '0.7rem', height: 20 }}
                                        />
                                    </Box>
                                    {mapping.Description && (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                            {mapping.Description}
                                        </Typography>
                                    )}
                                </Box>
                            ))}
                        </Box>
                    )}

                    {!isPublished && (
                        <Box sx={{ mt: 4 }}>
                            <Typography variant="subtitle1" sx={{ mb: 2, color: theme.palette.text.primary }}>
                                Available Labels
                            </Typography>
                            {availableLabels.length === 0 ? (
                                <Typography color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No published labels available to map to this collection point.
                                </Typography>
                            ) : (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                    {availableLabels.map((label) => (
                                    <Box key={label.LabelId} sx={{
                                        minWidth: 320,
                                        maxWidth: 400,
                                        flex: '1 1 320px',
                                        bgcolor: theme.palette.background.paper,
                                        borderRadius: theme.shape.borderRadius,
                                        boxShadow: theme.shadows[2],
                                        p: 3,
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 1.5,
                                        border: `1px solid ${theme.palette.divider}`,
                                        position: 'relative',
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Typography variant="h6" color="text.primary" sx={{ flex: 1 }}>{label.LabelKey}</Typography>
                                            <Button
                                                variant="contained"
                                                size="small"
                                                onClick={() => handleMapLabel(label.LabelId)}
                                                sx={{
                                                    bgcolor: theme.palette.primary.main,
                                                    color: theme.palette.primary.contrastText,
                                                    '&:hover': {
                                                        bgcolor: theme.palette.primary.dark,
                                                    },
                                                }}
                                            >
                                                Add
                                            </Button>
                                        </Box>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                            {label.LabelText}
                                        </Typography>
                                        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                                            <Chip 
                                                label={label.LabelType} 
                                                size="small" 
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                            <Chip 
                                                label={label.Category} 
                                                size="small" 
                                                variant="outlined"
                                                sx={{ fontSize: '0.7rem', height: 20 }}
                                            />
                                        </Box>
                                        {label.Description && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                                {label.Description}
                                            </Typography>
                                        )}
                                    </Box>
                                ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default LabelsTab; 